/**
 * Reusable Select UI Component
 */

import { useState, useRef, useEffect } from "react";
import type { SelectHTMLAttributes } from "react";

export interface SelectOption {
  readonly value: string;
  readonly label: React.ReactNode;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  readonly errorMessage?: string;
  readonly children?: React.ReactNode;
  readonly options?: SelectOption[];
}

export const Select = ({
  errorMessage,
  className,
  id,
  disabled,
  value,
  onChange,
  children,
  options: optionsProp,
}: SelectProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">("bottom");
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const helperId = errorMessage ? `${id ?? "select"}-error` : undefined;

  // Use options prop if provided, otherwise parse children
  let options: Array<{
    value: string;
    label: string | React.ReactNode;
    disabled?: boolean;
  }> = [];
  let selectedLabel: string | React.ReactNode = "";

  if (optionsProp && optionsProp.length > 0) {
    // Use options prop
    options = optionsProp;
    const selected = options.find((opt) => opt.value === value && !opt.disabled);
    selectedLabel = selected ? selected.label : "";
  } else if (children) {
    // Fallback to parsing children for backward compatibility
    const childArray = Array.isArray(children) ? children.flat() : [children];
    childArray.forEach((child) => {
      // Check if child is a valid React element with props
      if (child && typeof child === "object" && "props" in child) {
        const element = child as React.ReactElement<{
          value?: string;
          children?: React.ReactNode;
          disabled?: boolean;
        }>;
        const optionValue = String(element.props.value ?? "");
        const optionLabel =
          typeof element.props.children === "string"
            ? element.props.children
            : typeof element.props.children === "number"
              ? String(element.props.children)
              : String(element.props.children || "");
        const optionDisabled = element.props.disabled === true;

        if (optionValue || optionLabel) {
          options.push({
            value: optionValue,
            label: optionLabel,
            disabled: optionDisabled,
          });
          if (optionValue === value && !optionDisabled) {
            selectedLabel = optionLabel;
          }
        }
      }
    });
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current && dropdownRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.scrollHeight;
      const spaceBelow = window.innerHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      // If not enough space below but enough space above, show dropdown on top
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        // eslint-disable-next-line
        setDropdownPosition("top");
      } else {       
        setDropdownPosition("bottom");
      }
    }
  }, [isOpen]);

  const handleSelect = (option: (typeof options)[number]) => {
    // Note: Disabled options have their buttons disabled in the DOM,
    // so this handler is only called for enabled options
    if (onChange) {
      const event = {
        target: { value: option.value },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    }
    setIsOpen(false);
  };

  const buttonClasses = [
    "w-full px-4 py-2.5 rounded-lg border text-sm text-left bg-white transition-[border-color,box-shadow] duration-150 ease-in-out cursor-pointer appearance-none flex items-center justify-between",
    "focus:outline-none focus:border-[#001a41] focus:shadow-[0_0_0_1px_rgba(0,26,65,0.08)]",
    errorMessage ? "border-red-600" : "border-gray-300",
    disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "text-gray-900",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  // Get display label for button
  const displayLabel = selectedLabel || options[0]?.label || "Select...";
  const isPlaceholder = !selectedLabel;

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      <div className="relative">
        <button
          type="button"
          id={id}
          className={buttonClasses}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={helperId}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={isPlaceholder ? "text-gray-400" : ""}>
            {displayLabel}
          </span>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 20 20"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M6 8l4 4 4-4"
            />
          </svg>
        </button>

        {isOpen && !disabled && (
          <div 
            ref={dropdownRef}
            className={`absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto ${
              dropdownPosition === "top" ? "bottom-full mb-1" : "top-full mt-1"
            }`}
          >
            {options.map((option, index) => {
              const isSelected = option.value === value && !option.disabled;
              const isDisabled = option.disabled === true;

              return (
                <button
                  key={option.value || index}
                  type="button"
                  className={[
                    "w-full px-4 py-2.5 text-left text-sm transition-colors",
                    isDisabled
                      ? "text-gray-400 cursor-not-allowed bg-gray-50"
                      : isSelected
                        ? "bg-[#001a41] text-white"
                        : "text-gray-900 hover:bg-gray-50",
                  ].join(" ")}
                  onClick={() => handleSelect(option)}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {errorMessage && (
        <p id={helperId} className="text-xs text-red-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

